import locale from "@bonliva-crm/locale";
import { BulkActionSubType, SelectableBulkActionType } from "@bonliva-crm/shared";
import { Box, Step, StepButton, Stepper, makeStyles } from "@material-ui/core";
import { useCallback, useMemo } from "react";
import { BulkActionData } from "../utils/bulk-action-data";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { Steps } from "../utils/step";
import { ExternalSelectionStepProps, SelectionStep } from "./selection-step";

export type Props<
  RowType extends SelectionStepRowType,
  ActionType extends SelectableBulkActionType
> = {
  currentStep: Steps;
  setCurrentStep: (step: Steps) => void;
  selectedRows: RowType[];
  setSelectedRows: (rows: RowType[]) => void;
  type: ActionType;
  subType: (typeof BulkActionSubType)[ActionType];
  disabledSteps: { [key in Steps]?: boolean };
  stepProps: {
    [Steps.Selection]: ExternalSelectionStepProps<RowType>;
  };
  bulkActionData: Record<SelectableBulkActionType, BulkActionData>;
};

export const BulkActionModalContents = <
  RowType extends SelectionStepRowType,
  ActionType extends SelectableBulkActionType
>(
  props: Props<RowType, ActionType>
) => {
  const classes = useStyles();

  const selectionStep = useMemo(() => {
    return (
      <SelectionStep
        {...props.stepProps.Selection}
        type={props.type}
        selectedRows={props.selectedRows}
        setSelectedRows={props.setSelectedRows}
        bulkActionData={props.bulkActionData}
      />
    );
  }, [
    props.stepProps.Selection,
    props.type,
    props.selectedRows,
    props.setSelectedRows,
    props.bulkActionData
  ]);

  const actionStep = useMemo(() => {
    return props.bulkActionData[props.type].actionContent ? (
      <Box>{props.bulkActionData[props.type].actionContent}</Box>
    ) : null;
  }, [props.bulkActionData, props.type]);

  const confirmationStep = useMemo(() => {
    return <Box>{props.bulkActionData[props.type].confirmationContent}</Box>;
  }, [props.bulkActionData, props.type]);

  const steps = Object.values(Steps).filter((step) =>
    step === "Action" ? !!actionStep : true
  );
  const activeStep = steps.indexOf(props.currentStep);

  const getStepLabel = useCallback(
    (step: Steps) => {
      switch (step) {
        case Steps.Selection:
          return locale.bulkActionSteps.selection(
            props.bulkActionData[props.type].entityPlural
          );
        case Steps.Action:
          return locale.bulkActionSteps.action[props.type];
        case Steps.Confirmation:
          return locale.bulkActionSteps.confirmation;
      }
    },
    [props.bulkActionData, props.type]
  );

  const getStepContent = useCallback(
    (step: Steps) => {
      switch (step) {
        case Steps.Selection:
          return selectionStep;
        case Steps.Action:
          return actionStep;
        case Steps.Confirmation:
          return confirmationStep;
      }
    },
    [selectionStep, actionStep, confirmationStep]
  );

  return (
    <Box className={classes.root}>
      <Stepper
        activeStep={activeStep}
        className={classes.stepper}
        alternativeLabel
        nonLinear
      >
        {steps.map((step) => (
          <Step key={step}>
            <StepButton
              completed={steps.indexOf(step) < activeStep}
              disabled={props.disabledSteps[step]}
              onClick={() => props.setCurrentStep(step)}
            >
              {getStepLabel(step)}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Box className={classes.modalContentsComponentWrapper}>
        {getStepContent(steps[activeStep])}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    gap: "1rem"
  },
  stepper: {
    width: "100%",
    padding: 0
  },
  modalContentsComponentWrapper: {
    paddingTop: "12px",
    height: "100%",
    width: "100%",

    flexBasis: "70vh",
    paddingBottom: "60px"
  }
}));
